import React, {Fragment, useMemo} from 'react';
import {DataTablePage} from '@common/datatable/page/data-table-page';
import {Trans} from '@ui/i18n/trans';
import {DeleteSelectedItemsAction} from '@common/datatable/page/delete-selected-items-action';
import {DataTableEmptyStateMessage} from '@common/datatable/page/data-table-emty-state-message';
import movieNightImage from './movie-night.svg';
import {DataTableAddItemButton} from '@common/datatable/data-table-add-item-button';
import {Link, useParams} from 'react-router-dom';
import {useTitleIndexFilters} from '@app/titles/use-title-index-filters';
import {TitlesDatatableFilters} from '@app/admin/titles/titles-datatable-filters';
import {useTitle} from '@app/titles/requests/use-title';
import {Nzb} from '@app/titles/models/nzb';
import {ColumnConfig} from '@common/datatable/column-config';
import {ChipList} from '@ui/forms/input-field/chip-field/chip-list';
import {Chip} from '@ui/forms/input-field/chip-field/chip';
import {FormattedNumber} from '@ui/i18n/formatted-number';
import {FormattedDate} from '@ui/i18n/formatted-date';
import {EditIcon} from '@ui/icons/material/Edit';
import {EnumLinkStatusChip} from '@ui/i18n/enum-link-status';
import {EnumLinkQualityChip} from '@ui/i18n/enum-quality';
import {Tooltip} from '@ui/tooltip/tooltip';
import {IconButton} from '@ui/buttons/icon-button';
import {useLinkFiltersData} from '@app/liens/use-link-filters';
import {Title} from '@app/titles/models/title';
import {DialogTrigger} from '@ui/overlays/dialog/dialog-trigger';
import {Button} from '@ui/buttons/button';
import {PreviewDialog} from '@app/liens-reported/nzbs-list-datatable-page';

export function NzbListDatatablePageAdmin({
  loader,
  title,
}: {
  loader: string;
  title: Title;
}) {
  const {filters, filtersLoading} = useTitleIndexFilters();
  const filtersDefault = useLinkFiltersData();
  const {titleId} = useParams();

  const mergedFilters = useMemo(
    () => [...filters, ...TitlesDatatableFilters(filtersDefault)],
    [filters],
  );
  const actions: ColumnConfig<Nzb>[] = [
    {
      key: 'actions',
      header: () => <Trans message="Actions" />,
      hideHeader: true,
      visibleInMode: 'all',
      align: 'end',
      width: 'w-84 flex-shrink-0',
      body: nzb => (
        <Fragment>
          <Link
            to={`/admin/nzb/${nzb.title_id}/${nzb.id}/edit`}
            className="text-muted"
          >
            <Tooltip label={<Trans message="Edit" />}>
              <IconButton size="md">
                <EditIcon />
              </IconButton>
            </Tooltip>
          </Link>
        </Fragment>
      ),
    },
  ];

  function cutLongText(str: string) {
    return str.length > 50 ? str.substring(0, 50) + '...' : str;
  }

  const commonColumns: ColumnConfig<Nzb>[] = [
    {
      key: 'name',
      width: 'flex-3',
      visibleInMode: 'all',
      header: () => <Trans message="Hash" />,
      body: lien => <Fragment>{cutLongText(lien.name)}</Fragment>,
    },
    {
      key: 'active',
      allowsSorting: true,
      header: () => <Trans message="Status" />,
      body: nzb => <EnumLinkStatusChip status={nzb.active} />,
    },
    {
      key: 'qualite',
      allowsSorting: true,
      header: () => <Trans message="Quality" />,
      body: nzb => <EnumLinkQualityChip quality={nzb.qual?.qual} />,
      width: 'w-124 flex-shrink-0',
    },
    {
      key: 'langues_compact',
      visibleInMode: 'all',
      header: () => <Trans message="Languages" />,
      body: lien => {
        const langues = lien?.langues_compact || [];
        const displayedLangues = langues.slice(0, 2);
        const remainingLangues = langues
          .slice(2)
          .map(lang => lang.name)
          .join(', ');

        return (
          <>
            <ChipList radius="rounded" className="z-50" size="xs">
              <>
                {displayedLangues?.map(lang => (
                  <Chip key={lang.id} selectable>
                    <Trans message={lang.name} />
                  </Chip>
                ))}
                {langues?.length > 2 && (
                  <DialogTrigger type="modal">
                    <Button variant="link">
                      +{lien?.langues_compact.length} <Trans message={'more'} />
                    </Button>
                    <PreviewDialog remainingLangues={remainingLangues} />
                  </DialogTrigger>
                )}
              </>
            </ChipList>
          </>
        );
      },
    },
    {
      key: 'subs_compact',
      visibleInMode: 'all',
      header: () => <Trans message="Subtitles" />,
      body: lien => {
        const langues = lien?.subs_compact || [];
        const displayedLangues = langues.slice(0, 2);
        const remainingLangues = langues
          .slice(2)
          .map(lang => lang.name)
          .join(', ');

        return (
          <>
            <ChipList radius="rounded" className="z-50" size="xs">
              <>
                {displayedLangues?.map(lang => (
                  <Chip key={lang.id} selectable>
                    <Trans message={lang.name} />
                  </Chip>
                ))}
                {langues?.length > 2 && (
                  <DialogTrigger type="modal">
                    <Button variant="link">
                      +{lien?.subs_compact.length} <Trans message={'more'} />
                    </Button>
                    <PreviewDialog remainingLangues={remainingLangues} />
                  </DialogTrigger>
                )}
              </>
            </ChipList>
          </>
        );
      },
    },
    {
      key: 'reported',
      allowsSorting: true,
      header: () => <Trans message="Reports" />,
      body: nzb =>
        nzb.reports_count ? <FormattedNumber value={nzb.reports_count} /> : 0,
      width: 'w-124 flex-shrink-0',
    },
    {
      key: 'view',
      allowsSorting: true,
      header: () => <Trans message="Downloads" />,
      body: nzb => <FormattedNumber value={nzb.view} />,
      width: 'w-124 flex-shrink-0',
    },
  ];
  const LastUpdateColumn: ColumnConfig<Nzb>[] = [
    {
      key: 'updated_at',
      allowsSorting: true,
      width: 'w-124 flex-shrink-0',
      header: () => <Trans message="Last updated" />,
      body: nzb =>
        nzb.updated_at ? (
          <FormattedDate preset="short" date={nzb.updated_at} />
        ) : (
          ''
        ),
    },
  ];
  const NzbsListDatatableColumns: ColumnConfig<Nzb>[] = [
    ...commonColumns,
    ...LastUpdateColumn,
    ...actions,
  ];
  const NzbsListDatatableColumnsSerie: ColumnConfig<Nzb>[] = [
    ...commonColumns,
    {
      key: 'saison',
      allowsSorting: true,
      header: () => <Trans message="Season" />,
      body: nzb => <FormattedNumber value={nzb.saison} />,
    },
    {
      key: 'episodes',
      allowsSorting: true,
      header: () => <Trans message="Episode" />,
      body: nzb => (
        <ChipList radius="rounded" size="xs">
          {nzb?.episodes?.map(episode => (
            <Chip key={episode} selectable>
              {episode}
            </Chip>
          ))}
        </ChipList>
      ),
    },
    ...LastUpdateColumn,
    ...actions,
  ];

  return (
    <DataTablePage
      endpoint={'nzb'}
      queryParams={{title_id: titleId, loader: loader}}
      title={<Trans message="Nzb List" />}
      columns={
        title.is_series
          ? NzbsListDatatableColumnsSerie
          : NzbsListDatatableColumns
      }
      filters={mergedFilters}
      filtersLoading={filtersLoading}
      actions={<Actions />}
      selectedActions={<DeleteSelectedItemsAction />}
      emptyStateMessage={
        <DataTableEmptyStateMessage
          image={movieNightImage}
          title={<Trans message="No Nzb have been created yet" />}
          filteringTitle={<Trans message="No matching Nzb" />}
        />
      }
    />
  );
}

function Actions() {
  return (
    <Fragment>
      <DataTableAddItemButton elementType={Link} to="new">
        <Trans message="Add Nzb" />
      </DataTableAddItemButton>
    </Fragment>
  );
}
